import { getParameterByName } from '@shared/utils/UrlUtils';

// Sign-up funnel tracking for the marketing site's GA4 property and the Google Ads sign-up
// conversion. gtag is loaded in index.html, which also starts with GA4 switched off via the
// window['ga-disable-<id>'] flag. That flag is the only page-side control over the stream's
// enhanced measurement events, so trackPageView keeps it on for funnel routes only: GA4 must
// never see logged-in product usage. The same goes for the Ads tag, whose automatic page_view is
// switched off in index.html; trackPageView sends it on funnel routes for remarketing.

export type SignUpMethod = 'microsoft' | 'google' | 'email';

type Gtag = (...args: unknown[]) => void;

const GA4_ID = 'G-RMVGTLNF4S';
const ADS_ID = 'AW-10781300539';
const ADS_SIGN_UP_CONVERSION = `${ADS_ID}/5vTlCL7M_LgZELuu9pQo`;
const GA4_DISABLE_FLAG = `ga-disable-${GA4_ID}`;

// Logged-out auth screens plus the onboarding flow new accounts land on after sign-up.
const FUNNEL_ROUTES: ReadonlySet<string> = new Set([
  '/login',
  '/register',
  '/register/team-today',
  '/register/azure',
  '/verification-required',
  '/complete-registration',
  '/onboarding',
]);

// Query params that may reach GA4. Everything else is dropped, e.g. the email verification id
// on /complete-registration.
const ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'gclid', 'gbraid', 'wbraid', 'ad'];

const SIGN_UP_TRACKED_KEY_PREFIX = 'tt.signUpTracked.';
const DEBUG_SESSION_KEY = 'tt.gaDebug';
// Upper bound on how long a sign-up waits for gtag before the caller navigates away.
const EVENT_TIMEOUT_MS = 1000;
const GA4_PARAM_MAX_LENGTH = 100;

function getGtag(): Gtag | undefined {
  const gtag = (window as unknown as { gtag?: unknown }).gtag;
  return typeof gtag === 'function' ? (gtag as Gtag) : undefined;
}

function setGa4Enabled(enabled: boolean) {
  (window as unknown as Record<string, unknown>)[GA4_DISABLE_FLAG] = !enabled;
}

export function isFunnelRoute(pathname: string): boolean {
  const normalised = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return FUNNEL_ROUTES.has(normalised);
}

export function sanitisedPageLocation(pathname: string, search: string = window.location.search): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  ATTRIBUTION_PARAMS.forEach(name => {
    const value = params.get(name);
    if (value) kept.set(name, value);
  });
  const query = kept.toString();
  return `${window.location.origin}${pathname}${query ? `?${query}` : ''}`;
}

// ga_debug=1 is remembered for the tab so it survives the Microsoft redirect and in-app links.
function isDebugMode(): boolean {
  const inUrl = getParameterByName('ga_debug', window.location.href) === '1';
  try {
    if (inUrl) {
      sessionStorage.setItem(DEBUG_SESSION_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(DEBUG_SESSION_KEY) === '1';
  } catch (e) {
    return inUrl;
  }
}

// GA4 treats any debug_mode value as on, so the param is only ever added, never set to false.
function debugParams(): { debug_mode?: true } {
  return isDebugMode() ? { debug_mode: true } : {};
}

function sendEvent(gtag: Gtag, name: string, params: Record<string, unknown>): Promise<void> {
  return new Promise(resolve => {
    // event_callback never fires when gtag.js is blocked, so don't rely on it alone.
    const timer = window.setTimeout(resolve, EVENT_TIMEOUT_MS);
    gtag('event', name, {
      ...params,
      event_callback: () => {
        window.clearTimeout(timer);
        resolve();
      },
    });
  });
}

// Call on every router location change.
export function trackPageView(pathname: string): void {
  const onFunnel = isFunnelRoute(pathname);
  setGa4Enabled(onFunnel);

  const gtag = getGtag();
  if (!gtag) return;

  const pageLocation = sanitisedPageLocation(pathname);
  // Events gtag raises by itself (scroll, clicks, forms) would otherwise report the raw URL.
  gtag('set', { page_location: pageLocation });

  if (onFunnel) {
    gtag('event', 'page_view', { send_to: GA4_ID, page_location: pageLocation, ...debugParams() });
    gtag('event', 'page_view', { send_to: ADS_ID, page_location: pageLocation });
  }
}

function hasTrackedSignUp(accountId: string): boolean {
  try {
    return localStorage.getItem(SIGN_UP_TRACKED_KEY_PREFIX + accountId) !== null;
  } catch (e) {
    return false;
  }
}

function markSignUpTracked(accountId: string) {
  try {
    localStorage.setItem(SIGN_UP_TRACKED_KEY_PREFIX + accountId, new Date().toISOString());
  } catch (e) {
    // Storage can be blocked inside the Teams client; the backend only confirms a new account once anyway.
  }
}

// Call only once the backend has confirmed a NEW account was created. Never rejects, so callers can
// await it before navigating away without a try/catch.
export async function trackSignUp(method: SignUpMethod, accountId: string, campaignCode?: string | null): Promise<void> {
  const gtag = getGtag();
  // The register endpoints return the new user, so a missing id means creation wasn't confirmed:
  // agent resolves undefined instead of throwing for some failures (terminated request, 401, MSAL errors).
  if (!gtag || !accountId || hasTrackedSignUp(accountId)) return;
  markSignUpTracked(accountId);

  const campaign = campaignCode?.trim().slice(0, GA4_PARAM_MAX_LENGTH);
  const pageLocation = sanitisedPageLocation(window.location.pathname);

  // Sign-ups only happen on funnel routes, but don't let a stale flag drop the event.
  setGa4Enabled(true);
  try {
    await Promise.all([
      sendEvent(gtag, 'sign_up', {
        send_to: GA4_ID,
        method,
        page_location: pageLocation,
        ...(campaign ? { campaign_code: campaign } : {}),
        ...debugParams(),
      }),
      sendEvent(gtag, 'conversion', { send_to: ADS_SIGN_UP_CONVERSION, page_location: pageLocation }),
    ]);
  } catch (e) {
    // Analytics must never break registration.
  } finally {
    setGa4Enabled(isFunnelRoute(window.location.pathname));
  }
}
